import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

/**
 * Auto-refresh on 401:
 * - When any authenticated call fails with 401, hit /auth/refresh once.
 * - If refresh succeeds, retry the original request transparently.
 * - If refresh fails, bubble the 401 up so the UI can redirect to /login.
 *
 * We coalesce concurrent failures so a burst of expired-token requests only
 * triggers a single refresh round-trip.
 */
let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = (original.url || "").toString();

    // Skip refresh logic for the auth endpoints themselves to avoid loops.
    const isAuthCall =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");

    if (status !== 401 || isAuthCall || original.__retried) {
      return Promise.reject(error);
    }

    original.__retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      // New cookies are set by the server. Retry the original request.
      return api(original);
    } catch (refreshErr) {
      // Refresh failed → propagate the original 401.
      return Promise.reject(error);
    }
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
